<template>
    <div v-if="showToggle" class="dashboard-mode-toggle">
        <button
            class="toggle-button"
            :class="{ active: mode === 'yield' }"
            @click="switchToYield"
            :disabled="mode === 'yield'"
        >
            Yield
        </button>
        <button
            class="toggle-button"
            :class="{ active: mode === 'ltba' }"
            @click="switchToLtba"
            :disabled="mode === 'ltba'"
        >
            LTBA
        </button>
    </div>
</template>

<script>
import { computed } from "vue";
import { useRouter } from "vue-router";
import store from "@/store/store";
import { getterTypes, actionTypes } from "@/store/Publisher/types";

export default {
    name: "DashboardModeToggle",
    setup() {
        const router = useRouter();

        // Get current mode from store
        const mode = computed(() => store.getters[getterTypes.getSelectedMode]);

        // Get all publishers
        const publishers = computed(() => store.getters[getterTypes.getPublishers] || []);

        // Get LTBA and Yield publishers
        const ltbaPublishers = computed(() => publishers.value.filter((p) => !p.is_yield));
        const yieldPublishers = computed(() => publishers.value.filter((p) => p.is_yield === true));

        // Check if user has both types of publishers
        const hasLtbaPublishers = computed(() => ltbaPublishers.value.length > 0);
        const hasYieldPublishers = computed(() => yieldPublishers.value.length > 0);

        // Show toggle only if user has access to both types
        const showToggle = computed(() => hasLtbaPublishers.value && hasYieldPublishers.value);

        const switchToLtba = async () => {
            if (mode.value !== "ltba") {
                // Set mode in store
                store.dispatch(actionTypes.setSelectedMode, "ltba");

                // Keep LTBA state when switching back to LTBA mode

                // Get currently selected LTBA publisher or fallback to first
                const currentLtbaPublisher = store.getters[getterTypes.getSelectedPublisherLtba];
                const targetPublisher = currentLtbaPublisher || ltbaPublishers.value[0];

                if (targetPublisher) {
                    // Set/keep the publisher in store for LTBA mode (only if not already set)
                    if (!currentLtbaPublisher) {
                        store.dispatch(actionTypes.setSelectedPublisherLtba, targetPublisher);
                    }
                    // Navigate using Vue Router
                    await router.push(`/dashboard/ltba/${targetPublisher.id}`);
                } else {
                    await router.push("/dashboard/ltba");
                }
            }
        };

        const switchToYield = async () => {
            if (mode.value !== "yield") {
                // Set mode in store
                store.dispatch(actionTypes.setSelectedMode, "yield");

                // Keep or set Yield publishers (multi-publisher mode default)
                const currentYieldPublishers = store.getters[getterTypes.getSelectedPublishersYield];
                if (!currentYieldPublishers || currentYieldPublishers.length === 0) {
                    // Only set all publishers if none are selected
                    if (yieldPublishers.value.length > 0) {
                        store.dispatch(actionTypes.setSelectedPublishersYield, [...yieldPublishers.value]);
                    }
                }

                // Navigate using Vue Router
                await router.push("/dashboard/yield");
            }
        };

        return {
            showToggle,
            switchToLtba,
            switchToYield,
            mode,
        };
    },
};
</script>

<style lang="scss" scoped>
.dashboard-mode-toggle {
    display: inline-flex;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 2px;
    margin-right: 16px;

    .toggle-button {
        padding: 6px 16px;
        background: transparent;
        border: none;
        color: rgba(255, 255, 255, 0.7);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        border-radius: 3px;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &:hover:not(:disabled) {
            color: white;
            background: rgba(255, 255, 255, 0.1);
        }

        &.active {
            background: white;
            color: #2c3e50;
            cursor: default;
        }

        &:disabled {
            cursor: default;
        }

        &:not(:last-child) {
            margin-right: 2px;
        }
    }
}

body.variant-performax .dashboard-mode-toggle .toggle-button {
    color: rgba(0, 0, 0, 0.7);

    &:hover:not(:disabled) {
        color: rgba(0, 0, 0, 0.9);
    }

    &.active {
        background: $px-red;
        color: white;
    }
}
</style>
